<template>
    <GenericModal
        :modelValue="modelValue"
        @update:modelValue="$emit('update:modelValue', $event)"
        :closable="true"
        :fullscreenMobile="true"
        :darkMode="true"
        contentPadding="0"
    >
        <div class="category-modal-container">
            <div v-if="!selectedUnavailableCategory">
                <h2 class="category-title">
                    {{ $t('home.choose-type') }}
                </h2>
                <div class="category-grid">
                    <!-- Artists -->
                    <div
                        class="category-card"
                        @click="handleCategorySelect('artists')"
                    >
                        <div class="category-icon">
                            <img
                                src="@/assets/icons/category-artists.svg"
                                :alt="$t('home.category-artists')"
                            />
                        </div>
                        <span class="category-label">{{
                            $t('home.category-artists')
                        }}</span>
                    </div>

                    <!-- Lifestyle -->
                    <div
                        class="category-card"
                        @click="handleCategorySelect('lifestyle')"
                    >
                        <div class="category-icon">
                            <img
                                src="@/assets/icons/category-lifestyle.svg"
                                :alt="$t('home.category-lifestyle')"
                            />
                        </div>
                        <span class="category-label">{{
                            $t('home.category-lifestyle')
                        }}</span>
                    </div>

                    <!-- Experts -->
                    <div
                        class="category-card"
                        @click="handleCategorySelect('experts')"
                    >
                        <div class="category-icon">
                            <img
                                src="@/assets/icons/category-experts.svg"
                                :alt="$t('home.category-experts')"
                            />
                        </div>
                        <span class="category-label">{{
                            $t('home.category-experts')
                        }}</span>
                    </div>

                    <!-- NSFW -->
                    <div
                        class="category-card"
                        @click="handleCategorySelect('nsfw')"
                    >
                        <div class="category-icon">
                            <img
                                src="@/assets/icons/category-nsfw.svg"
                                :alt="$t('home.category-nsfw')"
                            />
                        </div>
                        <span class="category-label">{{
                            $t('home.category-nsfw')
                        }}</span>
                    </div>
                </div>
            </div>

            <!-- Unavailable Category Message -->
            <div v-else class="unavailable-message-container">
                <h2 class="unavailable-title">
                    {{ $t('home.category-unavailable-title') }}
                </h2>
                <p class="unavailable-description">
                    {{ $t('home.category-unavailable-description') }}
                </p>
                <div class="unavailable-actions">
                    <button
                        class="creator-link-button"
                        @click="handleGoToCreator"
                    >
                        {{ $t('home.category-go-to-creator') }}
                    </button>
                </div>
                <button class="back-button" @click="handleCloseMessage">
                    {{ $t('home.category-back') }}
                </button>
            </div>
        </div>
    </GenericModal>
</template>

<script setup>
import { ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import GenericModal from '@/components/shared/core/GenericModal.vue'

const router = useRouter()

const props = defineProps({
    modelValue: {
        type: Boolean,
        default: false,
    },
    // When true, the modal skips category selection and opens directly
    // on the closed-beta message (used by the home login button).
    directBeta: {
        type: Boolean,
        default: false,
    },
})

const emit = defineEmits(['update:modelValue', 'category-selected'])

const selectedUnavailableCategory = ref(null)

// In directBeta mode, jump straight to the unavailable message when opened.
watch(
    () => props.modelValue,
    (open) => {
        if (open && props.directBeta) {
            selectedUnavailableCategory.value = 'beta'
        } else if (!open) {
            selectedUnavailableCategory.value = null
        }
    },
    // immediate so it initializes correctly even when the modal is mounted
    // already-open (e.g. the global beta modal in App.vue).
    { immediate: true }
)

const handleCategorySelect = (category) => {
    emit('category-selected', category)
    // Always show the unavailable message regardless of category
    selectedUnavailableCategory.value = category
}

const handleGoToCreator = () => {
    emit('update:modelValue', false)
    router.push('/creator')
}

const handleCloseMessage = () => {
    if (props.directBeta) {
        // No category grid to return to — just close the modal.
        emit('update:modelValue', false)
    } else {
        selectedUnavailableCategory.value = null
    }
}
</script>

<style scoped>
/* Category Modal Styles */
.category-modal-container {
    width: 100%;
    height: 100%;
    padding: 100px 30px 40px;
    display: flex;
    flex-direction: column;
    align-items: center;
}

.category-title {
    font-family: 'Basier Circle', sans-serif;
    font-size: 30px;
    font-weight: 400;
    color: #ffffff;
    text-align: center;
    margin: 0 0 70px 0;
    line-height: 38px;
}

.category-grid {
    display: grid;
    grid-template-columns: repeat(2, 122px);
    grid-template-rows: repeat(2, 114px);
    gap: 9px;
    justify-content: center;
}

.category-card {
    background: rgba(217, 217, 217, 0.15);
    border-radius: 15px;
    padding: 20px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 12px;
    cursor: pointer;
    transition: all 0.2s ease;
}

.category-card:hover {
    background: rgba(217, 217, 217, 0.25);
    transform: translateY(-2px);
}

.category-icon {
    width: 47px;
    height: 47px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.category-icon img {
    width: 100%;
    height: 100%;
    object-fit: contain;
}

.category-label {
    font-family: 'Nunito', sans-serif;
    font-size: 15px;
    font-weight: 400;
    color: rgba(230, 230, 230, 1);
    text-align: center;
}

/* Unavailable Message Styles */
.unavailable-message-container {
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
}

.unavailable-title {
    font-family: 'Basier Circle', sans-serif;
    font-size: 28px;
    font-weight: 400;
    color: #ffffff;
    margin: 0 0 24px 0;
    line-height: 36px;
}

.unavailable-description {
    font-family: 'Nunito', sans-serif;
    font-size: 16px;
    font-weight: 400;
    color: rgba(230, 230, 230, 0.85);
    line-height: 24px;
    margin: 0 0 32px 0;
    max-width: 320px;
}

.unavailable-actions {
    margin-bottom: 16px;
}

.creator-link-button {
    background: rgba(255, 255, 255, 0.15);
    border: 1px solid rgba(255, 255, 255, 0.3);
    border-radius: 12px;
    padding: 14px 28px;
    font-family: 'Nunito', sans-serif;
    font-size: 15px;
    font-weight: 600;
    color: #ffffff;
    cursor: pointer;
    transition: all 0.2s ease;
}

.creator-link-button:hover {
    background: rgba(255, 255, 255, 0.25);
    transform: translateY(-1px);
}

.back-button {
    background: transparent;
    border: none;
    padding: 12px 24px;
    font-family: 'Nunito', sans-serif;
    font-size: 15px;
    font-weight: 600;
    color: rgba(230, 230, 230, 0.7);
    cursor: pointer;
    transition: color 0.2s ease;
}

.back-button:hover {
    color: rgba(230, 230, 230, 1);
}

/* Desktop Styles */
@media (min-width: 769px) {
    .category-modal-container {
        padding: 80px 40px 60px;
    }

    .category-title {
        font-size: 32px;
        margin-bottom: 80px;
    }

    .unavailable-title {
        font-size: 32px;
    }

    .unavailable-description {
        font-size: 17px;
        max-width: 400px;
    }
}
</style>
